import type {
  ChatPage,
  ChatParticipant,
  ChatsMetaQueryData,
  EditorRef, Name,
  NewChat
} from '../../../chats'
import type { FetchNextPageOptions, InfiniteData } from '@tanstack/react-query'
import type { FollowOutputCallback, VirtuosoHandle } from 'react-virtuoso'

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'
import { startOfDay } from 'date-fns'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
  MdArrowBack,
  MdClose,
  MdOutlineArchive,
  MdOutlineFilterAlt,
  MdOutlineFilterAltOff,
  MdOutlineFolderShared,
  MdOutlineMarkAsUnread,
  MdOutlineMarkChatUnread,
  MdOutlineUnarchive,
  MdPerson,
  MdSearch
} from 'react-icons/md'
import { useMatch, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import { animated, useSpring } from 'react-spring'
import { useClickAway } from 'react-use'
import { GroupedVirtuoso, Virtuoso } from 'react-virtuoso'
import { useThrottledCallback } from 'use-debounce'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../../lib/http'
import { useAppDispatch, useAppSelector } from '../../../../store'
import * as previewerActions from '../../../../store/modules/previewer'
import * as sidebarActions from '../../../../store/modules/sidebar'
import {
  ChatListItem,
  ChatReadStatus,
  ChatType,
  ChatTypeSelector,
  ContextMenu,
  getChatsMetaQueryKey,
  getChatsQueryKey,
  // getName,
  MessageComponent,
  MessageEditor,
  MessageGroupHeader,
  NameTag,
  nonNullablePredicate,
  ParticipantsAvatars,
  tagClassNameMapper,
  tagNameMapper,
  useChatMessagesQuery,
  useChatsMetaQuery,
  useChatsQuery,
  usePreviewQuery,
  useSendMessageMutation
} from '../../../chats'
import { NoDataPlaceHolder } from '../../../PatientFolder/behavioral/components'
import { usePatientQuery } from '../../../queries/patient'
import { useSidebar } from '../../context'

function transformName (name?: Name[]): string {
  if (!name) {
    return ''
  }

  return `${name?.[0]?.given?.[0] ?? ''} ${name?.[0]?.family?.[0] ?? ''}${name?.[0]?.suffix?.length ? `, ${name[0].suffix.join(', ')}` : ''}`
}

interface State {
  filters: {
    type: ChatType
    status?: ChatReadStatus
  }

  search: {
    query: string
    isVisible: boolean
  }

  pagination: {
    chats: {
      pageSize: number
    }
    messages: {
      pageSize: number
    }
  }

  selectedChat: {
    id?: string
    isVisible: boolean
  }

  newChat: {
    isVisible: boolean
    participantIds: Array<{ id: string, label: string }>
    includePatient: boolean
  }

  contextMenu: {
    chatId?: string
    isVisible: boolean
    x: number
    y: number
  }
}

type SupportsAndPractitioners = Array<{
  id: string
  name: Name[]
}>

type Action =
  | { type: 'filters/type/set', payload: { chatType: ChatType, stayInChat?: boolean }}
  | { type: 'filters/status/set', payload: ChatReadStatus }
  | { type: 'filters/status/clear' }
  | { type: 'search/query/set', payload: string }
  | { type: 'search/query/clear' }
  | { type: 'search/show' }
  | { type: 'search/hide' }
  | { type: 'search/toggle' }
  | { type: 'selected-chat/set', payload: { id: string } }
  | { type: 'selected-chat/hide' }
  | { type: 'selected-chat/clear' }
  | { type: 'new-chat/show' }
  | { type: 'new-chat/hide' }
  | { type: 'new-chat/toggle' }
  | { type: 'new-chat/reset' }
  | { type: 'new-chat/participants/add-current-patient' }
  | { type: 'new-chat/participants/add', payload: Option }
  | { type: 'new-chat/participants/remove', payload: string }
  | { type: 'new-chat/participants/clear' }
  | { type: 'context-menu/show', payload: { chatId: string, x: number, y: number } }
  | { type: 'context-menu/hide' }
  | { type: 'context-menu/move', payload: { x: number, y: number } }

let initialState: State = {
  filters: {
    type: ChatType.Own
  },
  search: {
    query: '',
    isVisible: false
  },
  pagination: {
    chats: {
      pageSize: 20
    },
    messages: {
      pageSize: 20
    }
  },
  selectedChat: {
    isVisible: false
  },
  newChat: {
    isVisible: false,
    participantIds: [],
    includePatient: false
  },
  contextMenu: {
    isVisible: false,
    x: 0,
    y: 0
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'search/query/set': {
      return {
        ...state,
        search: {
          ...state.search,
          query: action.payload
        }
      }
    }

    case 'filters/status/set': {
      return {
        ...state,
        filters: {
          ...state.filters,
          status: action.payload
        }
      }
    }

    case 'filters/status/clear': {
      return {
        ...state,
        filters: {
          ...state.filters,
          status: undefined
        }
      }
    }

    case 'search/query/clear': {
      return {
        ...state,
        search: {
          ...state.search,
          query: ''
        }
      }
    }

    case 'search/show': {
      return {
        ...state,
        search: {
          ...state.search,
          query: '',
          isVisible: true
        }
      }
    }

    case 'search/hide': {
      return {
        ...state,
        search: {
          ...state.search,
          isVisible: false
        }
      }
    }

    case 'search/toggle': {
      if (state.search.isVisible) {
        return reducer(state, { type: 'search/hide' })
      }

      return reducer(state, { type: 'search/show' })
    }

    case 'filters/type/set': {
      if (state.filters.type === action.payload.chatType) {
        return state
      }

      return {
        ...state,
        filters: {
          ...state.filters,
          type: action.payload.chatType
        },
        selectedChat: {
          isVisible: false
        },
        newChat: {
          ...state.newChat,
          isVisible: false
        }
      }
    }

    case 'selected-chat/set': {
      return {
        ...state,
        selectedChat: {
          id: action.payload.id,
          isVisible: true
        },
        newChat: {
          ...state.newChat,
          participantIds: [],
          includePatient: false,
          isVisible: false
        },
        search: {
          ...state.search,
          isVisible: false
        }
      }
    }

    case 'selected-chat/hide': {
      return {
        ...state,
        selectedChat: {
          ...state.selectedChat,
          isVisible: false
        }
      }
    }

    case 'selected-chat/clear': {
      return {
        ...state,
        selectedChat: {
          ...state.selectedChat,
          id: undefined,
          isVisible: false
        }
      }
    }

    case 'new-chat/show': {
      return {
        ...state,
        newChat: {
          ...state.newChat,
          isVisible: true
        },
        selectedChat: {
          ...state.selectedChat,
          isVisible: false
        },
        search: {
          ...state.search,
          isVisible: false
        }
      }
    }

    case 'new-chat/hide': {
      return {
        ...state,
        newChat: {
          ...state.newChat,
          isVisible: false
        }
      }
    }

    case 'new-chat/reset': {
      return {
        ...state,
        newChat: {
          participantIds: [],
          includePatient: false,
          isVisible: false
        }
      }
    }

    case 'new-chat/toggle': {
      return {
        ...state,
        newChat: {
          ...state.newChat,
          isVisible: !state.newChat.isVisible
        },
        selectedChat: {
          ...state.selectedChat,
          isVisible: !state.newChat.isVisible
            ? false
            : state.selectedChat.isVisible
        },
        search: {
          ...state.search,
          isVisible: !state.newChat.isVisible
            ? false
            : state.search.isVisible
        }
      }
    }

    case 'new-chat/participants/add': {
      return {
        ...state,
        newChat: {
          ...state.newChat,
          participantIds: [
            ...state.newChat.participantIds,
            action.payload
          ]
        }
      }
    }

    case 'new-chat/participants/add-current-patient': {
      return {
        ...state,
        newChat: {
          ...state.newChat,
          includePatient: !state.newChat.includePatient
        }
      }
    }

    case 'new-chat/participants/remove': {
      return {
        ...state,
        newChat: {
          ...state.newChat,
          participantIds: state.newChat.participantIds.filter(participant => participant.id !== action.payload)
        }
      }
    }

    case 'new-chat/participants/clear': {
      return {
        ...state,
        newChat: {
          ...state.newChat,
          participantIds: []
        }
      }
    }

    case 'context-menu/show': {
      return {
        ...state,
        contextMenu: {
          ...state.contextMenu,
          isVisible: true,
          chatId: action.payload.chatId,
          x: action.payload.x,
          y: action.payload.y
        }
      }
    }

    case 'context-menu/hide': {
      return {
        ...state,
        contextMenu: {
          ...state.contextMenu,
          isVisible: false
        }
      }
    }

    case 'context-menu/move': {
      return {
        ...state,
        contextMenu: {
          ...state.contextMenu,
          x: action.payload.x,
          y: action.payload.y
        }
      }
    }

    default: {
      return state
    }
  }
}

interface Option {id: string, label: string}

export function SidebarChats (): JSX.Element {
  let globallySelectedTab = useAppSelector(state => state.sidebar.chats.selectedTab)
  let globallySelectedChatId = useAppSelector(state => state.sidebar.chats.selectedChatId)
  let globallySelectedChatIdRef = useRef(globallySelectedChatId)
  let globallySelectedTabRef = useRef(globallySelectedTab)
  let globalDispatch = useAppDispatch()

  let [ state, dispatch ] = useReducer(reducer, {
    ...initialState,
    filters: {
      ...initialState.filters,
      type: globallySelectedTab ?? initialState.filters.type
    },
    selectedChat: {
      ...initialState.selectedChat,
      id: globallySelectedChatId ?? initialState.selectedChat.id,
      isVisible: typeof globallySelectedChatId === 'string' ? true : initialState.selectedChat.isVisible
    }
  })

  let [ participantSearch, setParticipantSearch ] = useState('')
  let selectedChatIdRef = useRef(state.selectedChat.id)
  let selectedTabRef = useRef(state.filters.type)
  let chatIsVisibleRef = useRef(state.selectedChat.isVisible)

  let userId = useAppSelector(state => state.auth?.profile?.sub)
  let queryClient = useQueryClient()
  let sidebar = useSidebar()

  let navigate = useNavigate()
  let editorRef = useRef<EditorRef>(null)

  let patientIdPathMatch = useMatch('/patient/:clientName/:patientId/*')
  let patientId = patientIdPathMatch?.params?.patientId
  let clientName = patientIdPathMatch?.params?.clientName

  let normalizedSearchQuery = state.search.query.trim()
  let [ debouncedSearchQuery ] = useDebounceValue(normalizedSearchQuery, 500)
  let [ debouncedParticipantSearch ] = useDebounceValue(participantSearch, 500)
  let searchInputRef = useRef<HTMLInputElement>(null)

  let contextMenuRef = useRef<HTMLDivElement>(null)
  let chatListRef = useRef<VirtuosoHandle>(null)
  let chatListScrollerRef = useRef<HTMLElement | Window | null>(null)
  let chatListScrollPosition = useRef<number>(0)

  useClickAway(contextMenuRef, () => {
    if (!state.contextMenu.isVisible) {
      return
    }

    dispatch({
      type: 'context-menu/hide'
    })
  })

  useEffect(() => {
    let isInvalid = !patientId && state.filters.type === ChatType.Current

    if (!isInvalid) {
      return
    }

    dispatch({
      type: 'filters/type/set',
      payload: {
        chatType: ChatType.Own
      }
    })
  }, [ patientId, state.filters.type ])

  let patientQuery = usePatientQuery({
    patientId: patientId ?? null
  })

  let chatsQuery = useChatsQuery({
    userId,
    patientId,
    searchQuery: debouncedSearchQuery,
    status: state.filters.status,
    type: state.filters.type
  })

  let messagesQuery = useChatMessagesQuery({
    userId,
    chatId: state.selectedChat?.id
  })

  let metaQuery = useChatsMetaQuery({
    userId,
    patientId
  })

  let chatsCount = chatsQuery.data?.pages.reduce((acc, page) => Number(acc) + Number(page.length), 0) ?? 0
  let messagesCount = messagesQuery.data?.pages.reduce((acc, page) => Number(acc) + Number(page.length), 0) ?? 0
  let lastMessage = messagesQuery.data?.pages?.flat().at(-1)

  let selectedChatQuery = useQuery<NewChat | undefined, unknown, NewChat | undefined, ['chat', { id?: string, userId?: string }]>({
    queryKey: [
      'chat',
      { id: state.selectedChat.id, userId }
    ],

    async queryFn ({ queryKey }) {
      let [ , { id, userId }] = queryKey

      if (!id || !userId) {
        return undefined
      }

      let params = new URLSearchParams([
        [ 'filters[id]', id ]
      ])

      let response = await http.getRequest(`/chats?${params.toString()}`)
      return response[0]
    },

    enabled: typeof userId === 'string' && typeof state.selectedChat.id === 'string',

    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchIntervalInBackground: false
  })

  let participantsQuery = useQuery<Option[] | undefined, unknown, Option[] | undefined, ['chat-participants', string]>({
    queryKey: [ 'chat-participants', debouncedParticipantSearch ],
    async queryFn ({ queryKey }) {
      const [ , participantSearch ] = queryKey

      return await getParticipants(participantSearch)
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false
  })

  let selectedChat = selectedChatQuery.data
  let contextMenuChat = useMemo(() => chatsQuery.data?.pages?.flat().find(chat => chat.id === state.contextMenu.chatId), [ chatsQuery.data?.pages, state.contextMenu.chatId ])
  let selectedChatMessagesCount = selectedChat?.messageCount ?? 0
  let totalMessagesCount = selectedChatMessagesCount ?? 1e12

  let patientIsPresent = selectedChat?.participants?.some(p => p.type === 'patient') ?? false

  let allParticipants = chatsQuery.data?.pages?.flat().reduce<ChatParticipant[]>((acc, chat) => [ ...acc, ...(chat.participants ?? []) ], []) ?? []

  let userMap = allParticipants.filter(nonNullablePredicate).reduce<Record<string, ChatParticipant>>((acc, user) => {
    return ({
      ...acc,
      [user.id]: user
    })
  }, {})

  let { groups, counts } = useMemo(() => {
    let map = new Map<string, number>()

    for (let page of messagesQuery.data?.pages ?? []) {
      for (let message of page) {
        let date = new Date(message.created_at ?? '')

        if (isNaN(date.getTime())) {
          continue
        }

        let key = startOfDay(date).toISOString()
        map.set(key, (map.get(key) ?? 0) + 1)
      }
    }

    let groups = [ ...map.keys() ]
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

    let counts = groups.map(group => map.get(group) ?? 0)

    return {
      groups,
      counts
    }
  }, [ messagesQuery.data?.pages ])

  let [ shouldAvoidScrolling, setShouldAvoidScrolling ] = useState(false)
  let [ secondPage, setSecondPage ] = useState<string>()

  let onFollowOutputHandler = useCallback<FollowOutputCallback>(isAtBottom => {
    if (shouldAvoidScrolling) {
      return false
    }

    if (
      isAtBottom ||
      (typeof userId === 'string' && lastMessage?.author === `Practitioner/${userId}`)
    ) {
      return 'auto'
    }

    return false
  }, [ lastMessage, userId, shouldAvoidScrolling ])

  let isChatOpen = (typeof state.selectedChat?.id === 'string' && state.selectedChat.isVisible) || state.newChat.isVisible

  useEffect(() => {
    if (!state.selectedChat.isVisible) {
      return
    }

    setSecondPage('chat')
  }, [ state.selectedChat.isVisible ])

  useEffect(() => {
    if (!state.newChat.isVisible) {
      return
    }

    setSecondPage('new-chat')
  }, [ state.newChat.isVisible ])

  let [ slidingContainerStyles ] = useSpring({
    transform: isChatOpen
      ? 'translateX(-50%)'
      : 'translateX(0%)'
  }, [ isChatOpen ])

  let scrollerWidth = chatListScrollerRef.current
    ? 'getBoundingClientRect' in chatListScrollerRef.current
      ? chatListScrollerRef.current.getBoundingClientRect().width
      : chatListScrollerRef.current.innerWidth
    : 0

  let archiveChatMutation = useMutation({
    async mutationFn ({ chatId }: { chatId: string }) {
      await http.postRequest(`/chats/${chatId}/archive`)
    },

    async onMutate ({ chatId }) {
      let chatsQueryKey = getChatsQueryKey({ userId, patientId })
      let metaQueryKey = getChatsMetaQueryKey({ userId, patientId })

      await queryClient.cancelQueries({ queryKey: metaQueryKey, exact: false })

      let previousChats = queryClient.getQueriesData<InfiniteData<ChatPage>>({ queryKey: chatsQueryKey, exact: false })
      let previousMeta = queryClient.getQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false })

      let chat = previousChats.map(([ _, data ]) => data).flatMap(data => data?.pages ?? []).flat().find(chat => chat.id === chatId)

      let unreadCount = chat?.unreadCount ?? 0

      let isMarkedUnread = chat?.unreadCount ?? false
      let isUnread = unreadCount > 0 || isMarkedUnread

      if (!isUnread) {
        return
      }

      queryClient.setQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false }, current => {
        if (!current) {
          return current
        }

        return {
          ...current,
          chats: {
            ...current.chats,
            [ChatType.Own]: {
              ...current.chats[ChatType.Own],
              unreadCount: Math.max(0, (current.chats[ChatType.Own]?.unreadCount ?? 0) - 1)
            },
            [ChatType.Archived]: {
              ...current.chats[ChatType.Archived],
              unreadCount: (current.chats[ChatType.Archived]?.unreadCount ?? 0) + 1
            }
          }
        }
      })

      return {
        previousMeta
      }
    },

    async onSettled (data, err, { chatId }, context) {
      // TODO: softer invalidation
      await queryClient.invalidateQueries({
        queryKey: getChatsQueryKey({ userId, patientId }),
        exact: false
      })

      await queryClient.invalidateQueries({
        queryKey: getChatsMetaQueryKey({ userId, patientId }),
        exact: false
      })
    },

    async onError (err, { chatId }, context) {
      void err

      if (context?.previousMeta) {
        for (let [ queryKey, data ] of context.previousMeta) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    }
  })

  let unarchiveChatMutation = useMutation({
    async mutationFn ({ chatId }: { chatId: string }) {
      await http.postRequest(`/chats/${chatId}/unarchive`)
    },

    async onMutate ({ chatId }) {
      let chatsQueryKey = getChatsQueryKey({ userId, patientId })
      let metaQueryKey = getChatsMetaQueryKey({ userId, patientId })

      await queryClient.cancelQueries({ queryKey: metaQueryKey, exact: false })

      let previousChats = queryClient.getQueriesData<InfiniteData<ChatPage>>({ queryKey: chatsQueryKey, exact: false })
      let previousMeta = queryClient.getQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false })

      let chat = previousChats.map(([ key, data ]) => data).flatMap(data => data?.pages ?? []).flat().find(chat => chat.id === chatId)

      let unreadCount = chat?.unreadCount ?? 0
      let isMarkedUnread = chat?.unread ?? false
      let isUnread = unreadCount > 0 || isMarkedUnread

      if (!isUnread) {
        return
      }

      queryClient.setQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false }, current => {
        if (!current) {
          return current
        }

        return {
          ...current,
          chats: {
            ...current.chats,
            [ChatType.Own]: {
              ...current.chats[ChatType.Own],
              unreadCount: (current.chats[ChatType.Own]?.unreadCount ?? 0) + 1
            },
            [ChatType.Archived]: {
              ...current.chats[ChatType.Archived],
              unreadCount: Math.max(0, (current.chats[ChatType.Archived]?.unreadCount ?? 0) - 1)
            }
          }
        }
      })

      return {
        previousMeta
      }
    },

    async onSettled (data, error, { chatId }, context) {
      // TODO: softer invalidation
      await queryClient.invalidateQueries({
        queryKey: getChatsQueryKey({ userId, patientId }),
        exact: false
      })

      await queryClient.invalidateQueries({
        queryKey: getChatsMetaQueryKey({ userId, patientId }),
        exact: false
      })
    },

    async onError (err, { chatId }, context) {
      void err

      if (context?.previousMeta) {
        for (let [ queryKey, data ] of context.previousMeta) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    }
  })

  let markChatAsReadMutation = useMutation({
    async mutationFn ({ chatId }: { chatId: string }) {
      await http.postRequest(`/chats/${chatId}/mark-as-read`)
    },

    async onMutate ({ chatId }) {
      let chatsQueryKey = getChatsQueryKey({ userId, patientId })
      let metaQueryKey = getChatsMetaQueryKey({ userId, patientId })

      await queryClient.cancelQueries({ queryKey: metaQueryKey, exact: false })

      let previousChats = queryClient.getQueriesData<InfiniteData<ChatPage>>({ queryKey: chatsQueryKey, exact: false })
      let previousMeta = queryClient.getQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false })

      let chat = previousChats.map(([ key, data ]) => data).flatMap(data => data?.pages ?? []).flat().find(chat => chat.id === chatId)

      let unreadCount = chat?.unreadCount ?? 0
      let isMarkedUnread = chat?.unread ?? false
      let isUnread = unreadCount > 0 || isMarkedUnread

      if (!isUnread) {
        return
      }

      queryClient.setQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false }, current => {
        if (!current) {
          return current
        }

        return {
          ...current,
          chats: {
            ...current.chats,
            [ChatType.Own]: {
              ...current.chats[ChatType.Own],
              unreadCount: chat?.archived ?? false
                ? current.chats[ChatType.Own]?.unreadCount ?? 0
                : Math.max(0, (current.chats[ChatType.Own]?.unreadCount ?? 0) - 1)
            },
            [ChatType.Archived]: {
              ...current.chats[ChatType.Archived],
              unreadCount: chat?.archived ?? false
                ? Math.max(0, (current.chats[ChatType.Archived]?.unreadCount ?? 0) - 1)
                : current.chats[ChatType.Archived]?.unreadCount ?? 0
            }
          }
        }
      })

      return {
        previousMeta
      }
    },

    async onSettled (data, error, { chatId }, context) {
      // TODO: softer invalidation
      await queryClient.invalidateQueries({
        queryKey: getChatsQueryKey({ userId, patientId }),
        exact: false
      })

      await queryClient.invalidateQueries({
        queryKey: getChatsMetaQueryKey({ userId, patientId }),
        exact: false
      })

      await queryClient.invalidateQueries({
        queryKey: [ 'chat', { userId, id: state.selectedChat.id }],
        exact: false
      })
    },

    async onError (err, { chatId }, context) {
      void err

      if (context?.previousMeta) {
        for (let [ queryKey, data ] of context.previousMeta) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    }
  })

  let markChatAsUnreadMutation = useMutation({
    async mutationFn ({ chatId }: { chatId: string }) {
      await http.postRequest(`/chats/${chatId}/mark-as-unread`)
    },

    async onMutate ({ chatId }) {
      let chatsQueryKey = getChatsQueryKey({ userId, patientId })
      let metaQueryKey = getChatsMetaQueryKey({ userId, patientId })

      await queryClient.cancelQueries({ queryKey: metaQueryKey, exact: false })

      let previousChats = queryClient.getQueriesData<InfiniteData<ChatPage>>({ queryKey: chatsQueryKey, exact: false })
      let previousMeta = queryClient.getQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false })

      let chat = previousChats.map(([ key, data ]) => data).flatMap(data => data?.pages ?? []).flat().find(chat => chat.id === chatId)

      let unreadCount = chat?.unreadCount ?? 0
      let isMarkedUnread = chat?.unread ?? false
      let isUnread = unreadCount > 0 || isMarkedUnread

      if (isUnread) {
        return
      }

      queryClient.setQueriesData<ChatsMetaQueryData>({ queryKey: metaQueryKey, exact: false }, current => {
        if (!current) {
          return current
        }

        return {
          ...current,
          chats: {
            ...current.chats,
            [ChatType.Own]: {
              ...current.chats[ChatType.Own],
              unreadCount: chat?.archived ?? false
                ? current.chats[ChatType.Own]?.unreadCount ?? 0
                : (current.chats[ChatType.Own]?.unreadCount ?? 0) + 1
            },
            [ChatType.Archived]: {
              ...current.chats[ChatType.Archived],
              unreadCount: chat?.archived ?? false
                ? (current.chats[ChatType.Archived]?.unreadCount ?? 0) + 1
                : current.chats[ChatType.Archived]?.unreadCount ?? 0
            }
          }
        }
      })

      return {
        previousMeta
      }
    },

    async onSettled (data, error, { chatId }, context) {
      // TODO: softer invalidation
      await queryClient.invalidateQueries({
        queryKey: getChatsQueryKey({ userId, patientId }),
        exact: false
      })

      await queryClient.invalidateQueries({
        queryKey: getChatsMetaQueryKey({ userId, patientId }),
        exact: false
      })
    },

    async onError (err, { chatId }, context) {
      void err

      if (context?.previousMeta) {
        for (let [ queryKey, data ] of context.previousMeta) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    }
  })

  let selectedChatClientName = selectedChat?.client ?? undefined

  let getDraftQuery = useQuery({
    queryKey: [
      'drafts',
      { path: 'chats', resource: state.selectedChat.id, userId, clientName: selectedChatClientName }
    ],

    async queryFn ({ queryKey, signal }) {
      let { path, resource, clientName } = queryKey[1] as QueryKeyParams<typeof queryKey>

      if (!clientName || !resource) {
        return null
      }

      let response = await http.getRequest(`/drafts/${path}/${resource}`, {}, {
        signal,
        clientName,
        validateStatus (status: number) {
          return (status >= 200 && status < 300) || status === 404
        }
      })

      const data = response.data ?? null

      let isEmpty = !data || (
        Array.isArray(data) &&
          data.length === 1 &&
          Array.isArray(data[0]?.children) &&
          data[0]?.children.length === 1 &&
          data[0]?.children[0]?.text?.trim() === ''
      )

      if (isEmpty) {
        return
      }

      editorRef.current?.setValue(data)

      return data
    },

    enabled: (
      typeof userId === 'string' &&
      typeof state.selectedChat.id === 'string' &&
      typeof selectedChatClientName === 'string'
    ),

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false
  })

  let updateDraftMutation = useMutation({
    async mutationFn (data: unknown) {
      let chatId = state.selectedChat.id
      let clientName = selectedChatClientName

      if (!chatId || !userId || !clientName) {
        return
      }

      await http.putRequest(`/drafts/chats/${chatId}`, { data }, {
        clientName
      })
    }
  })

  let deleteDraftMutation = useMutation({
    async mutationFn () {
      let chatId = state.selectedChat.id
      let clientName = selectedChatClientName

      if (!chatId || !userId || !clientName) {
        return
      }

      await http.deleteRequest(`/drafts/chats/${chatId}`, {}, {
        clientName
      })
    },

    async onMutate () {
      let chatId = state.selectedChat.id
      let clientName = selectedChatClientName

      if (!chatId || !userId || !clientName) {
        return
      }

      let queryKey = [
        'drafts',
        { path: 'chats', resource: chatId, userId, clientName }
      ]

      await queryClient.cancelQueries({ queryKey })

      let previousDraft = queryClient.getQueryData(queryKey)
      queryClient.setQueryData(queryKey, undefined)

      return {
        previousDraft
      }
    },

    async onError (err, variables, context) {
      void err

      let chatId = state.selectedChat.id
      let clientName = selectedChatClientName

      if (!chatId || !userId || !clientName) {
        return
      }

      if (!context?.previousDraft) {
        return
      }

      let queryKey = [
        'drafts',
        { path: 'chats', resource: chatId, userId, clientName }
      ]

      queryClient.setQueryData(queryKey, context?.previousDraft)
    }
  })

  let debouncedUpdateDraft = useThrottledCallback((payload: unknown) => {
    let isEmpty =
      Array.isArray(payload) &&
      payload.length === 1 &&
      Array.isArray(payload[0]?.children) &&
      payload[0]?.children.length === 1 &&
      payload[0]?.children[0]?.text?.trim() === ''

    if (isEmpty) {
      deleteDraftMutation.mutate()
      return
    }

    updateDraftMutation.mutate(payload)
  }, 1000)

  // let ackMutation = useAckMutation({
  //   userId,
  //   chatId: state.selectedChat?.id,
  //   patientId: selectedChat?.patient?.id,
  //   clientName: selectedChat?.client_id ?? undefined,
  //   participantId: (selectedChat?.meta?.forParticipant ?? '') || `Practitioner/${userId ?? ''}`
  // })

  let sendMessageMutation = useSendMessageMutation({
    userId,
    chatId: state.selectedChat.id,
    patientId: selectedChat?.patient?.id,
    clientName: selectedChat?.client ?? undefined,
    participantId: (selectedChat?.concierge ?? false)
      //   ToDo: check this field
      ? typeof metaQuery.data?.careteamMemberId === 'string'
        ? `Practitioner/${metaQuery.data.careteamMemberId}`
        : `Practitioner/${userId ?? ''}`
      : `Practitioner/${userId ?? ''}`
  })

  let { mutate: markChatAsRead } = markChatAsReadMutation

  useEffect(() => {
    if (
      typeof state.selectedChat.id !== 'string' ||
      !state.selectedChat.isVisible ||
      secondPage !== 'chat'
    ) {
      return
    }

    if ((selectedChat?.unreadCount ?? 0) > 0 || selectedChat?.unread) {
      markChatAsRead({
        chatId: state.selectedChat.id
      })
    }
  }, [ markChatAsRead, secondPage, selectedChat?.unread, selectedChat?.unreadCount, state.selectedChat.id, state.selectedChat.isVisible ])

  useEffect(() => {
    let hasChanged = globallySelectedChatId !== globallySelectedChatIdRef.current

    if (!hasChanged) {
      return
    }

    globallySelectedChatIdRef.current = globallySelectedChatId

    if (typeof globallySelectedChatId === 'string') {
      dispatch({
        type: 'selected-chat/set',
        payload: {
          id: globallySelectedChatId
        }
      })
    }

    if (typeof globallySelectedChatId !== 'string') {
      dispatch({
        type: 'selected-chat/hide'
      })
    }
  }, [ globallySelectedChatId ])

  useEffect(() => {
    let hasChanged =
      state.selectedChat.id !== selectedChatIdRef.current ||
      state.selectedChat.isVisible !== chatIsVisibleRef.current

    if (!hasChanged) {
      return
    }

    selectedChatIdRef.current = state.selectedChat.id
    chatIsVisibleRef.current = state.selectedChat.isVisible

    if (
      !state.selectedChat.isVisible ||
      typeof state.selectedChat.id !== 'string'
    ) {
      globalDispatch(sidebarActions.clearSelectedChat())
      return
    }

    globalDispatch(sidebarActions.selectChat(state.selectedChat.id))
  }, [ state.selectedChat.id, state.selectedChat.isVisible, globalDispatch ])

  useEffect(() => {
    let hasChanged = globallySelectedTab !== globallySelectedTabRef.current

    if (!hasChanged) {
      return
    }

    globallySelectedTabRef.current = globallySelectedTab

    if (typeof globallySelectedTab !== 'string') {
      return
    }

    selectedTabRef.current = globallySelectedTab

    dispatch({
      type: 'filters/type/set',
      payload: {
        chatType: globallySelectedTab
      }
    })
  }, [ globallySelectedTab ])

  useEffect(() => {
    let hasChanged = selectedTabRef.current !== state.filters.type

    if (!hasChanged) {
      return
    }

    selectedTabRef.current = state.filters.type
    globallySelectedTabRef.current = state.filters.type

    globalDispatch(sidebarActions.selectTab(state.filters.type))
  }, [ state.filters.type, globalDispatch ])

  async function getParticipants (name: string): Promise<Option[]> {
    if (!patientId) {
      return []
    }
    const response = await http.getRequest(`/chats/participants?patient-id=${patientId}&name=${name ?? ''}`)

    const values: Array<{id: string, label: string}> = []

    const practitioners = response.practitioners as unknown as SupportsAndPractitioners

    practitioners?.filter((p) => p.id !== userId ?? '').map((v) => {
      values.push({
        id: 'Practitioner/' + v.id,
        label: transformName(v.name)
      })

      return null
    })

    const supports = response?.supports as unknown as SupportsAndPractitioners

    supports?.map((v) => {
      values.push({
        id: 'RelatedPerson/' + v.id,
        label: transformName(v.name)
      })

      return null
    })

    return values
  }

  function createChat (): void {
    async function _createChat (): Promise<void> {
      const participants = state.newChat.participantIds.flatMap((x) => x.id)

      if (!userId) {
        return
      }

      if (!patientId) {
        return
      }

      participants.push('Practitioner/' + userId)

      if (state.newChat.includePatient) {
        participants.push('Patient/' + patientId)
      }

      if (participants.length <= 1) {
        return
      }

      const subject = 'no subject'

      const payload = {
        subject,
        participants
      }

      const newChat = await http.postRequest(`/patients/${patientId}/chats`, payload, { clientName })

      if (newChat.exist) {
        dispatch({ type: 'selected-chat/set', payload: { id: newChat.exist as string } })
        return
      }

      await queryClient.invalidateQueries({
        queryKey: getChatsQueryKey({ userId })
      })

      dispatch({ type: 'selected-chat/set', payload: { id: newChat.id as string } })

      setTimeout(() => {
        editorRef.current?.focus()
      }, 300)
    }

    _createChat().catch((e) => {
      console.error(e)
    })
  }

  let [ isAnimatingSearchInput, setIsAnimatingSearchInput ] = useState(false)

  let handleSearchInputAnimationRest = useCallback(() => {
    if (state.search.isVisible) {
      dispatch({
        type: 'filters/status/clear'
      })

      try {
        searchInputRef.current?.focus()
      } catch (err) {}
    }

    if (!state.search.isVisible) {
      dispatch({
        type: 'search/query/set',
        payload: ''
      })

      try {
        searchInputRef.current?.blur()
      } catch (err) {}
    }
  }, [ state.search.isVisible ])

  let [ panelHeaderStyle ] = useSpring({
    transform: state.search.isVisible
      ? 'translateX(-5rem)'
      : 'translateX(0rem)'
  }, [ state.search.isVisible ])

  let [ headerButtonsStyle ] = useSpring({
    transform: state.search.isVisible
      ? 'translateX(2.825rem)'
      : 'translateX(-0.75rem)'
  }, [ state.search.isVisible ])

  let [ nonSearchButtonsStyle ] = useSpring({
    transform: state.search.isVisible
      ? 'translateX(0.375rem)'
      : 'translateX(0rem)'
  }, [ state.search.isVisible ])

  let [ searchInputStyle ] = useSpring({
    opacity: state.search.isVisible
      ? 1
      : 0,

    transform: state.search.isVisible
      ? 'translateX(0rem)'
      : 'translateX(5rem)',

    onStart () {
      setIsAnimatingSearchInput(true)
    },

    onRest () {
      setIsAnimatingSearchInput(false)
      handleSearchInputAnimationRest()
    }
  }, [ state.search.isVisible ])

  useEffect(() => {
    if (typeof metaQuery.data?.careteamMemberId === 'string') {
      globalDispatch(sidebarActions.setCareTeamMemberId(metaQuery.data.careteamMemberId))
    }
  }, [ metaQuery.data?.careteamMemberId, globalDispatch ])

  return (
    <div className='tailwind flex-grow'>
      <div className='flex flex-col h-full'>
        <div className='flex items-center py-4 pl-4 pr-3 border-b border-b-gray-200 overflow-hidden max-w-full relative'>
          <div className='flex items-center flex-grow max-w-full border-red-400'>
            <div className='flex items-center overflow-hidden'>
              <animated.div
                className='text-lg w-20 flex-shrink-0'
                style={panelHeaderStyle}
              >
                Chats
              </animated.div>

              <animated.input
                type='text'
                className={cx(
                  'py-2 text-base rounded border-emerald-400 outline-none focus:ring-0 flex-grow flex-shrink-0 absolute left-0 top-0 h-full pl-4',
                  (!state.search.isVisible || isAnimatingSearchInput) && 'pointer-events-none'
                )}
                placeholder='Search chats'
                tabIndex={(state.search.isVisible && !isAnimatingSearchInput) ? 0 : -1}
                ref={searchInputRef}
                style={{
                  width: 'calc(100% - 2rem)',
                  ...searchInputStyle
                }}
                value={state.search.query}
                onChange={event => {
                  dispatch({
                    type: 'search/query/set',
                    payload: event.target.value
                  })
                }}
                onKeyUp={event => {
                  if (event.key === 'Escape') {
                    dispatch({
                      type: 'search/hide'
                    })

                    try {
                      searchInputRef.current?.blur()
                    } catch (err) {}
                  }
                }}
              />

            </div>
            <animated.div
              className='flex gap-[3px] items-center absolute top-0 right-0 h-full'
              style={headerButtonsStyle}
            >
              <button
                type='button'
                title='Search chats'
                className='p-2 -m-2 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors'
                onClick={() => {
                  dispatch({
                    type: 'search/toggle'
                  })
                }}
              >
                {!state.search.isVisible && (
                  <MdSearch
                    size={21}
                    className='text-inherit translate-y-[1px]'
                  />
                )}

                {state.search.isVisible && (
                  <MdClose
                    size={21}
                    className='text-inherit'
                  />
                )}
              </button>

              <animated.div
                className={cx('flex flex-row items-center gap-[3px]', state.search.isVisible && 'pointer-events-none')}
                style={nonSearchButtonsStyle}
              >
                <button
                  type='button'
                  className={cx(
                    'flex flex-row justify-center items-center p-1',
                    state.filters.status === ChatReadStatus.Unread && 'text-blue-600 hover:text-blue-500',
                    typeof state.filters.status === 'undefined' && 'text-gray-400 hover:text-gray-700'
                  )}
                  title={
                    state.filters.status === ChatReadStatus.Unread
                      ? 'Show all chats'
                      : 'Show only unread chats'
                  }
                  onClick={() => {
                    if (state.filters.status === ChatReadStatus.Unread) {
                      dispatch({
                        type: 'filters/status/clear'
                      })

                      return
                    }

                    dispatch({
                      type: 'filters/status/set',
                      payload: ChatReadStatus.Unread
                    })
                  }}
                >
                  {state.filters.status === ChatReadStatus.Unread && (
                    <MdOutlineFilterAltOff
                      size={21}
                    />
                  )}

                  {typeof state.filters.status === 'undefined' && (
                    <MdOutlineFilterAlt
                      size={21}
                    />
                  )}
                </button>

                <button
                  type='button'
                  title='Close panel'
                  className='p-2 -m-2 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors'
                  onClick={() => {
                    sidebar.setState(null)
                  }}
                >
                  <MdClose
                    size={22}
                    className='text-inherit'
                  />
                </button>
              </animated.div>
            </animated.div>
          </div>
        </div>

        <div className='flex flex-col pt-1 border-b border-gray-200'>
          <div className='flex flex-row flex-grow max-w-full pr-2'>
            <ChatTypeSelector
              value={state.filters.type}
              options={[
                {
                  type: ChatType.Own,
                  label: 'My Chats',
                  unreadCount: metaQuery.data?.chats[ChatType.Own]?.unreadCount ?? 0
                },
                ...(typeof patientId === 'string'
                  ? [
                    {
                      type: ChatType.Current,
                      label: 'Current',
                      unreadCount: metaQuery.data?.chats[ChatType.Current]?.unreadCount ?? 0
                    }
                  ]
                  : []
                ),
                {
                  type: ChatType.Concierge,
                  label: 'Concierge',
                  unreadCount: metaQuery.data?.chats[ChatType.Concierge]?.unreadCount ?? 0
                },
                {
                  type: ChatType.Archived,
                  title: 'Archived chats',
                  label: (
                    <MdOutlineArchive
                      size={20}
                      className='-mt-0.5 text-gray-400'
                    />
                  ),
                  unreadCount: metaQuery.data?.chats[ChatType.Archived]?.unreadCount ?? 0
                }
              ]}
              onChange={chatType => {
                dispatch({
                  type: 'filters/type/set',
                  payload: { chatType: chatType as ChatType }
                })
              }}
            />
          </div>
        </div>

        <div className='h-full flex flex-col overflow-hidden'>
          <animated.div
            className='w-[200%] grid grid-cols-2 flex-grow'
            style={slidingContainerStyles}
          >
            {
              state.filters.type !== ChatType.Current &&
              chatsCount === 0 &&
              state.filters.status !== ChatReadStatus.Unread && (
                <div className='flex flex-row w-full justify-center'>
                  <NoDataPlaceHolder title='No chats' />
                </div>
              )
            }

            {state.filters.type !== ChatType.Current && chatsCount === 0 && state.filters.status === ChatReadStatus.Unread && (
              <div className='flex flex-row w-full justify-center'>
                <NoDataPlaceHolder
                  style={{ margin: '0 -2rem' }}
                  title={'Awesome! You\'ve read all the messages in your inbox.'}
                />
              </div>
            )}

            {state.filters.type === ChatType.Current && typeof patientId === 'undefined' && (
              <div className='flex flex-row w-full justify-center'>
                <NoDataPlaceHolder title='Open a patient to see your chats with them' />
              </div>
            )}

            {state.filters.type === ChatType.Current && typeof patientId === 'string' && chatsCount === 0 && (
              <div className='flex flex-row w-full justify-center'>
                <NoDataPlaceHolder title='No chats'>
                  <button
                    type='button'
                    className='uppercase font-roboto font-medium py-2 px-4 flex flex-row items-center justify-center gap-1 bg-blue-500 text-white rounded'
                    onClick={() => {
                      dispatch({
                        type: 'new-chat/show'
                      })
                    }}
                  >
                    Create a Chat
                  </button>
                </NoDataPlaceHolder>
              </div>
            )}

            {(
              (state.filters.type === ChatType.Current && typeof patientId === 'string' && chatsCount > 0) ||
              (state.filters.type !== ChatType.Current && chatsCount > 0)
            ) && (
              <div className='flex flex-col relative'>
                {state.filters.type === ChatType.Archived && (
                  <div className='flex flex-row justify-center items-center bg-gray-200 py-2 px-4 font-roboto font-medium uppercase text-xs text-gray-600 border-b border-gray-200'>
                    Archived chats
                  </div>
                )}

                {state.filters.type === ChatType.Current && typeof patientId === 'string' && (
                  <button
                    type='button'
                    className='new-chat-sidebar-button flex flex-row justify-start items-center bg-[#EBF0FC] text-[#3464E3] py-2 px-4 font-roboto font-semibold tracking-wider uppercase text-xs border-b border-gray-200'
                    onClick={() => {
                      dispatch({
                        type: 'new-chat/show'
                      })
                    }}
                    data-test-id='new-chat-sidebar-button'
                  >
                    Create chat
                  </button>
                )}

                <Virtuoso
                  totalCount={chatsCount}
                  ref={chatListRef}
                  components={{ Footer }}
                  context={{ hasNewPage: chatsQuery.hasNextPage, onShowMore: chatsQuery?.fetchNextPage }}
                  scrollerRef={el => {
                    chatListScrollerRef.current = el
                  }}
                  onScroll={event => {
                    if (state.contextMenu.isVisible) {
                      event.currentTarget.scrollTo({
                        top: chatListScrollPosition.current
                      })

                      return
                    }

                    chatListScrollPosition.current = event.currentTarget.scrollTop
                  }}
                  endReached={() => {
                    if (chatsQuery.hasNextPage) {
                      void chatsQuery.fetchNextPage()
                    }
                  }}
                  // overscan={200}
                  itemContent={index => {
                    let chat = chatsQuery.data?.pages?.flat()[index]

                    if (!chat) {
                      return (
                        <div>
                          <h2>Loading...</h2>
                        </div>
                      )
                    }

                    if (!chat.patient) {
                      return (
                        <div>
                          <h2>Chat without patient</h2>
                        </div>
                      )
                    }

                    let readAt: Date | undefined
                    let patientReadAt: Date | undefined
                    let ticks: number | undefined

                    if (
                      chat.concierge &&
                      chat.lastMessage?.author !== `Patient/${chat.patient.id}`
                    ) {
                      let patientAck = chat?.participants?.find(p => p.id === chat?.patient?.id ?? '')

                      readAt = patientAck?.offsetUpdatedAt && patientAck?.messageOffset === chat?.messageCount
                        ? new Date(patientAck.offsetUpdatedAt)
                        : undefined

                      patientReadAt = readAt

                      ticks = readAt && !isNaN(readAt.getTime())
                        ? 2
                        : undefined
                    }

                    if (
                      !chat.concierge &&
                      chat.participants?.length === 2 &&
                      chat.lastMessage?.author === `Practitioner/${userId ?? ''}`
                    ) {
                      let patientAck = chat?.participants?.find(p => p.type === 'patient')

                      if (patientAck?.offsetUpdatedAt && patientAck?.messageOffset === chat?.messageCount) {
                        readAt = new Date(patientAck.offsetUpdatedAt)
                        patientReadAt = typeof patientAck?.messageOffset === 'number' && typeof patientAck.offsetUpdatedAt === 'string'
                          ? new Date(patientAck.offsetUpdatedAt)
                          : undefined
                        ticks = 2
                      }
                    }

                    if (
                      !chat.concierge &&
                      (chat.participants?.length ?? 0) > 2 &&
                        chat.lastMessage?.author !== `Patient/${chat?.patient?.id}`
                    ) {
                      let patientAck = chat?.participants?.find(p => p.id === chat?.patient?.id ?? '')
                      let patientIsParticipant = chat.participants?.some(p => p.id === chat?.patient?.id) ?? false
                      let patientHasRead = patientIsParticipant && patientAck?.messageOffset === chat?.messageCount

                      let nonPatientParticipants = chat.participants?.filter(p => p.id !== chat?.patient?.id ?? '') ?? []
                      let othersHaveRead = nonPatientParticipants.every(p => p?.messageOffset === chat?.messageCount)

                      patientReadAt = patientHasRead && typeof patientAck?.messageOffset === 'number'
                        ? new Date(patientAck.messageOffset)
                        : undefined

                      if (patientHasRead && !othersHaveRead) {
                        readAt = new Date(patientAck?.offsetUpdatedAt ?? '')
                        ticks = 1
                      }

                      if (!patientHasRead && othersHaveRead) {
                        let dates = nonPatientParticipants
                          .map(p => p.offsetUpdatedAt)
                          .map(date => new Date(date as string))
                          .filter(date => !isNaN(date.getTime()))
                          .sort((a, b) => b.getTime() - a.getTime())

                        readAt = dates[0]
                        ticks = 1

                        if (!patientIsParticipant) {
                          ticks = 2
                        }
                      }

                      if (patientHasRead && othersHaveRead) {
                        let dates = (chat.participants ?? [])
                          .map(p => p.offsetUpdatedAt ?? '')
                          .map(date => new Date(date))
                          .filter(date => !isNaN(date.getTime()))
                          .sort((a, b) => b.getTime() - a.getTime())

                        readAt = dates[0]
                        ticks = 2
                      }
                    }

                    return (
                      <ChatListItem
                        key={chat.id}
                        isActive={state.selectedChat?.id === chat.id}
                        hasUnreadMessages={(chat?.unreadCount ?? 0) > 0}
                        isMarkedUnread={chat?.unread ?? false}
                        onContextMenu={event => {
                          if (!event.isDefaultPrevented()) {
                            event.preventDefault()
                          }

                          if (!event.isPropagationStopped()) {
                            event.stopPropagation()
                          }

                          if (!chat) {
                            return
                          }

                          if (state.contextMenu.isVisible && state.contextMenu.chatId === chat.id) {
                            dispatch({
                              type: 'context-menu/hide'
                            })

                            return
                          }

                          dispatch({
                            type: 'context-menu/show',
                            payload: {
                              chatId: chat.id,
                              ...getContextMenuPosition({
                                event,
                                container: chatListScrollerRef.current
                              })
                            }
                          })
                        }}
                        onClick={() => {
                          if (!chat) {
                            return
                          }

                          dispatch({
                            type: 'selected-chat/set',
                            payload: {
                              id: chat.id
                            }
                          })

                          // editorRef.current?.focus()

                          setTimeout(() => {
                            editorRef.current?.focus()
                          }, 300)
                        }}
                        patient={chat.patient}
                        participants={
                          (chat.participants ?? [])
                            .filter(nonNullablePredicate)
                            .filter(p => {
                              if ((chat?.concierge ?? false) && typeof metaQuery.data?.careteamMemberId === 'string') {
                                return p.id !== metaQuery.data.careteamMemberId
                              }

                              return true
                            })
                            .map(p => {
                              let readAt: Date | undefined

                              if (p.offsetUpdatedAt && p.messageOffset === chat?.messageCount) {
                                readAt = new Date(p.offsetUpdatedAt)
                              }

                              return ({
                                ...p,
                                readAt
                              })
                            })
                        }
                        excludedParticipantAvatarIds={chat.concierge ? undefined : [ userId ?? '' ]}
                        lastMessage={(() => {
                          let message = chat.lastMessage

                          if (!message) {
                            return undefined
                          }

                          let authorId = message.author?.split('/')[1] ?? ''

                          let author: ChatParticipant = userMap?.[authorId] ?? {
                            id: '',
                            name: 'N/A',
                            type: 'practitioner'
                          }

                          return {
                            id: message?.id ?? '',
                            author,
                            content: (
                              <ListItemLastMessageContent
                                chat={chat}
                                userId={
                                  (chat.concierge ?? false)
                                    ? metaQuery.data?.careteamMemberId ?? userId
                                    : userId
                                }
                              />
                            ),
                            sentAt: new Date(message?.createdAt ?? '')
                          }
                        })()}

                        readAt={readAt}
                        patientReadAt={patientReadAt}
                        readTicks={ticks}
                        isConcierge={chat.concierge ?? false}
                        tags={chat?.tags ?? undefined}
                      />
                    )
                  }}
                />

                {state.contextMenu.isVisible && (
                  <ContextMenu
                    position={{
                      x: state.contextMenu.x,
                      y: state.contextMenu.y
                    }}
                    direction={
                      state.contextMenu.x > scrollerWidth / 2 ? 'bottom-left' : 'bottom-right'
                    }
                    actions={[
                      {
                        id: 'patient-folder',
                        label: 'Open Patient Folder',
                        icon: MdOutlineFolderShared,
                        onClick () {
                          if (!state.contextMenu.chatId) {
                            return
                          }

                          let chat = chatsQuery.data?.pages?.flat().find(chat => chat.id === state.contextMenu.chatId)
                          let patientId = chat?.patient?.id ?? undefined
                          let clientName = chat?.client ?? undefined

                          if (!patientId || !clientName) {
                            return
                          }

                          let url = `/patient/${clientName}/${patientId}/patientFolder`
                          navigate(url)

                          dispatch({
                            type: 'context-menu/hide'
                          })
                        }
                      },
                      ...(
                        (contextMenuChat?.unread ?? false) || (contextMenuChat?.unreadCount ?? 0) > 0
                          ? [
                            {
                              id: 'mark-as-read',
                              label: 'Mark as read',
                              icon: MdOutlineMarkAsUnread,
                              onClick () {
                                if (!state.contextMenu.chatId) {
                                  return
                                }

                                markChatAsReadMutation.mutate({
                                  chatId: state.contextMenu.chatId
                                })

                                dispatch({
                                  type: 'context-menu/hide'
                                })
                              }
                            }
                          ]
                          : (contextMenuChat?.messageCount ?? 0) > 0
                            ? [
                              {
                                id: 'mark-as-unread',
                                label: 'Mark as unread',
                                icon: MdOutlineMarkChatUnread,
                                onClick () {
                                  if (!state.contextMenu.chatId) {
                                    return
                                  }

                                  markChatAsUnreadMutation.mutate({
                                    chatId: state.contextMenu.chatId
                                  })

                                  dispatch({
                                    type: 'context-menu/hide'
                                  })
                                }
                              }
                            ]
                            : []
                      ),
                      ...(
                        contextMenuChat?.archived ?? false
                          ? [
                            {
                              id: 'unarchive',
                              label: 'Unarchive',
                              icon: MdOutlineUnarchive,
                              onClick () {
                                if (!state.contextMenu.chatId) {
                                  return
                                }

                                unarchiveChatMutation.mutate({
                                  chatId: state.contextMenu.chatId
                                })

                                dispatch({
                                  type: 'context-menu/hide'
                                })
                              }
                            }
                          ]
                          : []
                      ),
                      ...(
                        (contextMenuChat?.active ?? false) && !(contextMenuChat?.archived ?? false) && !(contextMenuChat?.concierge ?? false)
                          ? [
                            {
                              id: 'archive',
                              label: 'Archive',
                              icon: MdOutlineArchive,
                              onClick () {
                                if (!state.contextMenu.chatId) {
                                  return
                                }

                                archiveChatMutation.mutate({
                                  chatId: state.contextMenu.chatId
                                })

                                dispatch({
                                  type: 'context-menu/hide'
                                })
                              }
                            }
                          ]
                          : []
                      )
                      // {
                      //   id: 'all-chats-with-patient',
                      //   label: 'All chats with patient',
                      //   icon: MdOutlineChat,
                      //   onClick: () => {
                      //     // TODO: Implement
                      //   }
                      // },

                    ]}
                    onClose={() => {
                      dispatch({
                        type: 'context-menu/hide'
                      })
                    }}
                  />
                )}
              </div>
            )}

            {secondPage === 'chat' && (
              <div className='flex flex-col'>
                <div className='flex flex-row border-b border-gray-200'>
                  <div className='flex flex-col w-full'>
                    <div className='flex flex-row items-center justify-center py-4 px-12 relative'>
                      <button
                        type='button'
                        className='p-2 absolute left-1 top-[8px]'
                        onClick={() => {
                          dispatch({
                            type: 'selected-chat/hide'
                          })
                        }}
                      >
                        <MdArrowBack
                          size={20}
                          className='text-gray-800'
                        />
                      </button>

                      <NameTag
                        isAbsent={!patientIsPresent}
                        className={cx(patientIsPresent && 'pt-[1px]', 'cursor-pointer')}
                        title={selectedChat?.patient ? 'Open Patient Folder' : undefined}
                        data-test-id='patient-name-tag'
                        onClick={() => {
                          if (!selectedChat?.patient || !selectedChat.client) {
                            return
                          }

                          let url = `/patient/${selectedChat.client}/${selectedChat.patient.id}/patientFolder`
                          navigate(url)
                        }}
                      >
                        {selectedChat?.patient && selectedChat.patient.name}
                      </NameTag>

                      <ParticipantsAvatars
                        className='absolute right-4 top-[13px]'
                        participants={
                          (selectedChat?.participants ?? [])
                            .filter((p) => p.visible)
                            .sort((a, b) => (a.type === 'patient' ? 0 : 1) - (b.type === 'patient' ? 0 : 1))
                            // .map(p => p.id)
                            // .map(id => userMap?.[id])
                            .filter(nonNullablePredicate)
                            .filter(p => {
                              if ((selectedChat?.concierge ?? false) && typeof metaQuery.data?.careteamMemberId === 'string') {
                                return p.id !== metaQuery.data.careteamMemberId
                              }

                              return true
                            })
                            .map(p => {
                              let readAt: Date | undefined

                              if (p.offsetUpdatedAt && p.messageOffset === selectedChat?.messageCount) {
                                readAt = new Date(p.offsetUpdatedAt)
                              }

                              return ({
                                ...p,
                                readAt
                              })
                            })
                        }
                        excludedIds={selectedChat?.concierge ? undefined : [ userId ?? '' ]}
                        // direction='right'
                      />

                    </div>

                    {selectedChat?.concierge && (selectedChat?.tags?.length ?? 0) > 0 && (
                      <div className='flex flex-row flex-wrap no-scrollbar px-4 pb-3 gap-2 items-center justify-center'>
                        {selectedChat?.tags?.map(tag => (
                          <div
                            key={tag}
                            className={cx(
                              'whitespace-nowrap p-1 px-1.5 text-xs font-medium font-roboto',
                              tagClassNameMapper[tag] ?? 'text-slate-600 bg-gray-100'
                            )}
                          >
                            {tagNameMapper[tag] ?? tag}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {messagesCount <= 0 && (
                  <NoDataPlaceHolder
                    title='No Messages'
                    className='-mx-10'
                  />
                )}

                <GroupedVirtuoso
                  groupCounts={counts}
                  firstItemIndex={messagesCount > 0 ? Math.max(0, totalMessagesCount - messagesCount) : 0}
                  initialTopMostItemIndex={messagesCount > 0 ? messagesCount - 1 : 0}
                  followOutput={onFollowOutputHandler}
                  startReached={() => {
                    if (messagesQuery.hasNextPage) {
                      setShouldAvoidScrolling(true)

                      messagesQuery.fetchNextPage()
                        .catch(err => {
                          console.error(err)
                        })
                        .finally(() => {
                          setShouldAvoidScrolling(false)
                        })
                    }
                  }}
                  groupContent={index => {
                    let date = new Date(groups[index] ?? '')

                    return (
                      <MessageGroupHeader>
                        {!isNaN(date.getTime()) && (
                          date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
                        )}

                        {isNaN(date.getTime()) && (
                          'N/A'
                        )}
                      </MessageGroupHeader>
                    )
                  }}
                  itemContent={index => {
                    let actualIndex = totalMessagesCount - index - 1
                    let message = messagesQuery.data?.pages?.flat()[actualIndex]

                    let chat = selectedChatQuery.data ?? chatsQuery.data?.pages?.flat().find(chat => chat.id === state.selectedChat?.id)
                    let justTheTwoOfUs = chat?.participants?.length === 2 && chat?.participants?.some((p) => p.id === message?.author)
                    let displayAuthor = !justTheTwoOfUs || true
                    let senderId = chat?.concierge === true && typeof message?.concierge === 'object' && message.concierge !== null
                      ? message.concierge.id
                      : message?.author?.split('/')[1] ?? ''

                    // let sender = selectedChatParticipantsMap[senderId] ?? userMap[senderId]
                    let sender = userMap[senderId]
                    let attachment = message?.attachments?.[0]

                    let participantId = (chat?.concierge ?? false)
                      ? metaQuery.data?.careteamMemberId ?? userId
                      : userId

                    let isPatient = senderId === chat?.patient?.id
                    let isAuthor = typeof userId === 'string' && senderId === userId

                    return (
                      <MessageComponent
                        author={displayAuthor ? userMap[message?.author?.split('/')[1] ?? ''] : undefined}
                        // author={displayAuthor ? selectedChatParticipantsMap[message?.author?.split('/')[1] ?? ''] : undefined}
                        name={typeof userId === 'string' && userId === senderId ? 'You' : sender ? sender.name : 'N/A'}
                        isAuthor={isAuthor}
                        isPatient={isPatient}
                        sentAt={new Date(message?.created_at ?? '')}
                        isBeingSent={sendMessageMutation.isPending && message?.id.startsWith('local')}
                        attachment={attachment}
                        chatId={chat?.id ?? ''}
                        patientId={chat?.patient?.id ?? ''}
                        participantId={`Practitioner/${participantId ?? ''}`}
                        clientId={chat?.client ?? ''}
                        tags={message?.tags ?? undefined}
                        onPreviewClick={() => {
                          if (attachment) {
                            globalDispatch(previewerActions.open({
                              mimeType: attachment.mime_type as string,
                              name: attachment.filename,
                              id: attachment.id
                            }))
                          }
                        }}
                      >
                        {message?.body}
                      </MessageComponent>
                    )
                  }}
                />

                {patientIsPresent && (
                  <div className='flex flex-col'>
                    <div className='flex flex-row items-center justify-center py-2 px-4 gap-1.5 bg-amber-100 text-yellow-600 shadow-gray-100'>
                      <MdPerson
                        size={18}
                      />

                      <span className='font-roboto uppercase text-xs font-medium'>
                        Chat with member
                      </span>
                    </div>
                  </div>
                )}

                <div className='flex flex-col gap-2 border-t-x border-t-gray-200 focus-within:border-t-violet-200-x transition-colors'>
                  <MessageEditor
                    ref={editorRef}
                    chatId={state.selectedChat?.id ?? ''}
                    isDisabled={getDraftQuery.isFetching}
                    onChange={value => {
                      if (!getDraftQuery.isFetching) {
                        debouncedUpdateDraft(value)
                      }
                    }}
                    onSubmit={(message, { revertValue }) => {
                      sendMessageMutation.mutate({
                        text: message.text,
                        // @ts-expect-error // It's fine
                        file: message.files[0]
                      }, {
                        onSuccess () {
                          deleteDraftMutation.mutate()
                        },

                        onError (error) {
                          void error
                          revertValue()
                        }
                      })
                    }}
                  />
                </div>
              </div>
            )}

            {secondPage === 'new-chat' && (
              <div className='flex flex-col'>
                <div className='flex flex-col px-4 py-4 border-b border-gray-200'>
                  <Select
                    className='flex-grow'
                    placeholder='Search for a participant'
                    options={
                      (participantsQuery?.data ?? [])
                        .filter((r) => !state.newChat.participantIds.some((participants) => participants.id === r.id))
                        .sort((a, b) => {
                          if (a.label < b.label) return -1
                          if (a.label > b.label) return 1
                          return 0
                        })
                    }
                    filterOption={null}
                    value={null}
                    onInputChange={(value) => setParticipantSearch(value)}
                    isLoading={participantsQuery.isLoading}
                    blurInputOnSelect={false}
                    onChange={value => {
                      if (!value || typeof value !== 'object') {
                        return
                      }
                      if ('id' in value && 'label' in value) {
                        const data = value as unknown as Option
                        if (state.newChat.participantIds.some((p) => p.id === data.id)) {
                          dispatch({
                            type: 'new-chat/participants/remove',
                            payload: data.id
                          })
                          return
                        }

                        dispatch({
                          type: 'new-chat/participants/add',
                          payload: data
                        })
                      }
                    }}
                  />

                  <div
                    className={cx('flex flex-row pt-3', {
                      'animate-pulse': patientQuery.isPending
                    })}
                  >
                    <label className='flex flex-row justify-start items-center'>
                      <input
                        onChange={() => dispatch({ type: 'new-chat/participants/add-current-patient' })}
                        checked={state.newChat.includePatient}
                        disabled={patientQuery.status !== 'success'}
                        type='checkbox'
                        className='form-checkbox mr-2 rounded text-violet-600 border outline-none focus:ring-0'
                      />

                      <span className='font-roboto text-sm'>
                        Include member <span className='font-medium'>{patientQuery.isSuccess && transformName(patientQuery.data?.name)}</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className='flex flex-col'>
                  {state.newChat.participantIds.map((participant) => (
                    <div
                      className='flex flex-row items-center justify-between pl-4 pr-2 py-1 border-b border-gray-200'
                      key={participant.id}
                    >
                      <span className='font-robot text-sm'>
                        {participant.label}
                      </span>

                      <button
                        type='button'
                        className='p-2'
                        onClick={() => {
                          dispatch({
                            type: 'new-chat/participants/remove',
                            payload: participant.id
                          })
                        }}
                      >
                        <MdClose
                          size={20}
                          className='text-gray-500'
                        />
                      </button>
                    </div>
                  ))}

                  <div className='flex flex-row py-6 px-4 gap-4'>
                    <button
                      type='button'
                      className='font-roboto uppercase text-center font-medium text-sm rounded py-2 px-4'
                      onClick={() => {
                        dispatch({
                          type: 'new-chat/hide'
                        })
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      disabled={!state.newChat.includePatient && (state.newChat.participantIds ?? []).length <= 0}
                      type='button'
                      className='font-roboto uppercase text-center font-medium text-sm rounded py-2 flex-grow bg-blue-500 text-white disabled:bg-[#f4f4f4] disabled:text-[#9a9797] disabled:cursor-not-allowed transition-all'
                      onClick={createChat}
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}
          </animated.div>
        </div>
      </div>
    </div>
  )
}

interface Position {
  x: number
  y: number
}

interface GetContextMenuPositionOptions {
  container: HTMLElement | Window | null
  event: Pick<React.MouseEvent<HTMLDivElement | HTMLButtonElement, MouseEvent>, 'clientX' | 'clientY'>
}

function getContextMenuPosition ({ event, container }: GetContextMenuPositionOptions): Position {
  if (!container || (!('getBoundingClientRect' in container))) {
    return {
      x: 0,
      y: 0
    }
  }

  let rect = container.getBoundingClientRect()
  let x = event.clientX - rect.left
  let y = event.clientY - rect.top

  return {
    x,
    y
  }
}

function ListItemLastMessageContent ({ chat, userId }: {chat: NewChat, userId?: string}): JSX.Element {
  const lastMessage = chat.lastMessage

  if (!lastMessage) {
    return (
      <p className='font-roboto font-normal text-sm line-clamp-1 text-gray-400'>
        No messages yet
      </p>
    )
  }

  if (lastMessage?.attachment) {
    return (
      <LastMessageImagePreview
        chat={chat}
        userId={userId}
      />
    )
  }

  if (typeof lastMessage?.body === 'string' && lastMessage.body.length > 0) {
    return (
      <p className='font-roboto font-normal text-sm line-clamp-1 text-gray-600'>
        {lastMessage?.body}
      </p>
    )
  }

  return (
    <p className='font-roboto font-normal text-sm line-clamp-1 text-gray-600'>
      N/A
    </p>
  )
}

function LastMessageImagePreview ({ chat, userId }: { chat: NewChat, userId?: string }): JSX.Element | null {
  const chatId = chat.id
  const participantId = `Practitioner/${userId ?? ''}`
  const patientId = chat.patient?.id ?? ''
  const clientId = chat.client ?? ''
  const lastMessage = chat.lastMessage
  const attachment = lastMessage?.attachment

  let previewQuery = usePreviewQuery({
    participantId,
    patientId,
    chatId,
    clientId,
    attachment
  })

  const message = typeof lastMessage?.body === 'string' && lastMessage.body.length > 0 ? lastMessage.body : (attachment?.filename ?? 'Attachment')

  return (
    <div className='flex flex-row gap-1 items-center w-full'>
      {previewQuery.status !== 'success' && (
        <div className='h-[15px] w-[15px] rounded bg-gray-500 animate-pulse' />
      )}

      {previewQuery.status === 'success' && (
        <img
          className='h-[15px] w-[15px] object-cover rounded'
          src={previewQuery.data?.src}
          alt='preview-for-chat'
        />
      )}

      <p className='font-roboto font-normal text-sm line-clamp-1 text-gray-600'>
        {message}
      </p>
    </div>
  )
}

type QueryKeyParams<T> = T extends Array<string | infer P> ? P : never

interface FooterProps {
  context?: {
    hasNewPage?: boolean | undefined
    onShowMore: (options?: (FetchNextPageOptions | undefined)) => Promise<unknown>
  } | undefined
}

function Footer ({ context }: FooterProps): JSX.Element | null {
  function showMore (): void {
    async function fn (): Promise<void> {
      await context?.onShowMore?.()
    }

    fn().catch((e) => {
      console.error(e)
    })
  }

  return (
    <div className={cx('text-center', !context?.hasNewPage && 'py-6')}>
      {!context?.hasNewPage && (
        <span className='text-gray-400'>All chats loaded</span>
      )}

      {context?.hasNewPage && (
        <button
          type='button'
          onClick={showMore}
          className='py-4 w-full bg-primary bg-opacity-10 text-primary'
        >
          Show More
        </button>
      )}
    </div>
  )
}
